<template>
  <div class="card mb-2 route-stop" :class="{ 'route-done': stop.done, 'route-overdue': overdue }">
    <div class="card-body py-2">
      <div class="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <span v-if="index" class="route-index">{{ index }}</span>
          <span class="badge me-2" :class="tipo.classe">{{ tipo.label }}</span>
          <strong>{{ tipo.momento }} {{ stop.time }}</strong>
          <span v-if="overdue" class="text-danger small fw-semibold"> · era {{ formatarData(stop.date) }}</span>
          <span v-else-if="stop.event_date !== stop.date" class="text-muted small"> ({{ formatarData(stop.event_date) }})</span>
          <span v-if="stop.done" class="badge text-bg-light border ms-2">Concluída</span>
        </div>
        <div class="text-end d-flex align-items-center gap-2">
          <span class="route-status" :class="'route-status-' + statusSlug">{{ statusTexto }}</span>
          <span v-if="showValues && stop.amount_due > 0" class="fw-bold">A cobrar: {{ money(stop.amount_due) }}</span>
        </div>
      </div>

      <div class="mt-1">
        <i class="bi bi-person me-1"></i>{{ stop.client.name ?? 'Cliente' }}
        <a v-if="stop.client.phone" :href="'tel:' + stop.client.phone" class="ms-2 d-print-none">{{ stop.client.phone }}</a>
        <span v-if="stop.client.phone" class="d-none d-print-inline ms-2">{{ stop.client.phone }}</span>
      </div>
      <div v-if="stop.counter"><i class="bi bi-shop me-1"></i>No balcão</div>
      <div v-else><i class="bi bi-geo-alt me-1"></i>{{ stop.address || 'Endereço não informado' }}</div>
      <div class="small text-muted mt-1">
        {{ stop.items.map(it => it.quantity + 'x ' + it.name).join(', ') }}
      </div>
      <div v-if="stop.note" class="small mt-1"><i class="bi bi-sticky me-1"></i>{{ stop.note }}</div>

      <div class="d-flex flex-wrap gap-2 mt-2 d-print-none">
        <template v-if="!stop.counter && destino">
          <a :href="linkMapa" target="_blank" rel="noopener" class="btn btn-sm btn-outline-secondary"><i class="bi bi-map me-1"></i>Google Maps</a>
          <a :href="linkWaze" target="_blank" rel="noopener" class="btn btn-sm btn-outline-secondary"><i class="bi bi-cursor me-1"></i>Waze</a>
        </template>
        <router-link :to="{ path: '/rental/details', query: { id: stop.rental_id } }" class="btn btn-sm btn-outline-primary">
          Locação {{ stop.code || '#' + stop.rental_id }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import statusFormat from '@/services/statusFormat';

const TIPOS = {
  DELIVERY:    { label: 'Entrega',             classe: 'text-bg-primary',   momento: 'Festa às' },
  PICKUP:      { label: 'Coleta',              classe: 'text-bg-warning',   momento: 'Festa até' },
  COUNTER_OUT: { label: 'Retirada no balcão',  classe: 'text-bg-info',      momento: 'Retira às' },
  COUNTER_IN:  { label: 'Devolução no balcão', classe: 'text-bg-secondary', momento: 'Devolve até' },
};

// Destino para mapas: coordenadas quando houver, senão o endereço
export function destinoParada(stop) {
  if (stop.counter) return null;
  if (stop.latitude && stop.longitude) return `${stop.latitude},${stop.longitude}`;
  return stop.address || null;
}

export default {
  name: 'StopCard',
  props: {
    stop: { type: Object, required: true },
    index: { type: Number, default: null },
    overdue: { type: Boolean, default: false },
    // Operador não vê valores (mesma regra do antigo Resumo)
    showValues: { type: Boolean, default: true },
  },
  computed: {
    tipo() {
      return TIPOS[this.stop.type] ?? TIPOS.DELIVERY;
    },
    statusTexto() {
      return statusFormat.status(this.stop.status);
    },
    statusSlug() {
      return String(this.stop.status || '').toLowerCase().replace(/_/g, '-');
    },
    destino() {
      return destinoParada(this.stop);
    },
    linkMapa() {
      return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(this.destino);
    },
    linkWaze() {
      if (this.stop.latitude && this.stop.longitude) return `https://waze.com/ul?ll=${this.stop.latitude},${this.stop.longitude}&navigate=yes`;
      return 'https://waze.com/ul?q=' + encodeURIComponent(this.destino) + '&navigate=yes';
    },
  },
  methods: {
    formatarData(iso) {
      return iso ? iso.split('-').reverse().join('/') : '';
    },
    money(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
  },
}
</script>

<style scoped>
.route-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  margin-right: .5rem;
  border-radius: 50%;
  background: #0F1117;
  color: #fff;
  font-size: .8rem;
  font-weight: 600;
}

.route-done {
  opacity: .6;
}

.route-overdue {
  border-left: 4px solid #dc3545;
}

/* Cores de status vêm das variáveis --status-color-* em assets/main.scss */
.route-status {
  font-size: .75rem;
  font-weight: 600;
  padding: .15rem .55rem;
  border-radius: 999px;
  color: #fff;
  background: var(--status-color-unknown);
}

.route-status-pending-delivery       { background: var(--status-color-pending-delivery); }
.route-status-pending-pickup         { background: var(--status-color-pending-pickup); }
.route-status-active                 { background: var(--status-color-active); }
.route-status-pending-company-pickup { background: var(--status-color-pending-company-pickup); }
.route-status-pending-return         { background: var(--status-color-pending-return); }
.route-status-completed,
.route-status-force-completed        { background: var(--status-color-completed); }

@media print {
  .route-status {
    color: #000;
    background: none !important;
    border: 1px solid #999;
  }
}
</style>
